import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  FiGrid,
  FiList,
  FiZap,
  FiSend,
  FiCheckCircle,
  FiPlusCircle,
  FiActivity,
  FiCamera,
  FiMessageCircle,
  FiCalendar,
  FiFileText,
  FiBriefcase,
  FiUser,
  FiMenu,
  FiX,
  FiCreditCard,
  FiTruck,
} from "react-icons/fi";
import { getDashboardStats } from "../../utils/missionsApi";
import MissionsLogo from "./components/MissionsLogo";
import "./MissionsLayout.css";

/* ============================================================
   Sidebar definition — two sections sharing the same UI.
   The "Pro Management" section links to existing pages so users
   keep their bearings; the "ProMeet Missions" section is the new
   marketplace.
============================================================ */
const NAV_PRO_MANAGEMENT = [
  { key: "dashboard",     label: "Tableau de bord",  to: "/pro/dashboard",         icon: <FiGrid /> },
  { key: "appointments",  label: "Rendez-vous",      to: "/pro/calendar",          icon: <FiCalendar /> },
  { key: "customers",     label: "Mes RDV clients",  to: "/client/appointments",   icon: <FiUser /> },
  { key: "quotes",        label: "Devis / Factures", to: "/pro/documents/me",      icon: <FiFileText />, useUserId: true },
  { key: "documents",     label: "Documents",        to: "/pro/documents/me",      icon: <FiBriefcase />, useUserId: true },
  { key: "subscription",  label: "Abonnement",       to: "/billing/subscription",  icon: <FiCreditCard /> },
];

const NAV_MISSIONS = [
  { key: "m-dashboard",  label: "Tableau de bord",  to: "/missions/dashboard",   icon: <FiGrid /> },
  { key: "m-list",       label: "Missions dispo",   to: "/missions/list",        icon: <FiList /> },
  { key: "m-urgent",     label: "Missions urgentes",to: "/missions/urgent",      icon: <FiZap />, alert: true },
  { key: "m-mine",       label: "Mes candidatures", to: "/missions/applications",icon: <FiSend /> },
  { key: "m-accepted",   label: "Missions acceptées",to: "/missions/accepted",   icon: <FiCheckCircle /> },
  { key: "m-create",     label: "Poster une mission",to: "/missions/create",     icon: <FiPlusCircle /> },
  { key: "m-tracking",   label: "Suivi des missions",to: "/missions/tracking",   icon: <FiActivity /> },
  { key: "m-photos",     label: "Photos / Validation",to: "/missions/photos",    icon: <FiCamera /> },
  { key: "m-messages",   label: "Messages",          to: "/missions/messages",   icon: <FiMessageCircle /> },
];

const NAV_ADMIN_MISSIONS = [
  { key: "admin-missions", label: "Validation / attribution", to: "/admin/missions", icon: <FiBriefcase /> },
];

function NavItem({ item, active, badge, onClick, sectionMissions }) {
  return (
    <button
      type="button"
      className={`pmm-nav-item ${active ? "is-active" : ""}`}
      data-section={sectionMissions ? "missions" : "pms"}
      onClick={onClick}
    >
      <span className="pmm-nav-icon">{item.icon}</span>
      <span>{item.label}</span>
      {Number.isFinite(badge) && badge > 0 && (
        <span className={`pmm-nav-badge ${item.alert ? "pmm-nav-badge--alert" : ""}`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}

export default function MissionsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";
  const proCanPostMissions = localStorage.getItem("proCanPostMissions") === "true";

  const loadStats = useCallback(async () => {
    try {
      const s = await getDashboardStats();
      setStats(s);
    } catch (_) {
      setStats(null);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, location.pathname]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const resolveTo = useCallback((item) => {
    if (item.useUserId && userId) return item.to.replace("/me", `/${userId}`);
    return item.to;
  }, [userId]);

  const isActive = useCallback((to) => {
    if (!to) return false;
    const p = location.pathname;
    if (to === "/missions/dashboard") return p === to || p === "/missions";
    return p === to || p.startsWith(to + "/");
  }, [location.pathname]);

  const badgeFor = useCallback((key) => {
    if (!stats) return undefined;
    switch (key) {
      case "m-urgent":      return stats.urgentNearby || 0;
      case "m-mine":        return stats.myProposals || 0;
      case "m-accepted":    return stats.activeAsProvider || 0;
      case "m-tracking":    return (stats.activeAsClient || 0) + (stats.activeAsProvider || 0);
      default: return undefined;
    }
  }, [stats]);

  const missionNav = useMemo(() => {
    if (role === "partner") return NAV_MISSIONS.filter((item) => item.key !== "m-create");
    if (role === "pro") {
      if (!proCanPostMissions) return [];
      return NAV_MISSIONS.filter((item) =>
        ["m-dashboard", "m-create", "m-tracking", "m-photos", "m-messages"].includes(item.key)
      );
    }
    return NAV_MISSIONS;
  }, [role, proCanPostMissions]);

  const sidebar = useMemo(() => (
    <aside className={`pmm-sidebar ${sidebarOpen ? "is-open" : ""}`}>
      <div className="pmm-brand">
        <MissionsLogo size={40} />
        <div className="pmm-brand-text">
          <strong>ProMeet <span style={{ color: "var(--color-primary-hover)" }}>Missions</span></strong>
          <span>Marketplace automobile B2B</span>
        </div>
      </div>

      {role !== "partner" && (
      <div className="pmm-section">
        <div className="pmm-section-title">Pro Management</div>
        {NAV_PRO_MANAGEMENT.map((item) => (
          <NavItem
            key={item.key}
            item={item}
            active={isActive(resolveTo(item))}
            onClick={() => navigate(resolveTo(item))}
          />
        ))}
      </div>
      )}

      <div className="pmm-section pmm-section--missions">
        <div className="pmm-section-title">ProMeet Missions</div>
        {missionNav.map((item) => (
          <NavItem
            key={item.key}
            item={item}
            active={isActive(item.to)}
            badge={badgeFor(item.key)}
            sectionMissions
            onClick={() => navigate(item.to)}
          />
        ))}
      </div>

      {role === "admin" && (
        <div className="pmm-section">
          <div className="pmm-section-title">Admin missions</div>
          {NAV_ADMIN_MISSIONS.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              active={isActive(item.to)}
              onClick={() => navigate(item.to)}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: "auto", padding: "8px 4px 0", borderTop: "1px dashed var(--border)" }}>
        <div className="pmm-chip pmm-chip--brand" style={{ width: "100%", justifyContent: "center" }}>
          <FiTruck /> Mode{" "}
          {role === "pro" ? "Client pro" : role === "partner" ? "Prestataire Partner" : role === "admin" ? "Admin" : "Client"}
        </div>
      </div>
    </aside>
  ), [sidebarOpen, role, missionNav, navigate, isActive, resolveTo, badgeFor]);

  return (
    <div className="pmm-shell">
      {sidebar}
      {sidebarOpen && (
        <div className="pmm-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <main className="pmm-main">
        <div className="pmm-topbar">
          <button
            type="button"
            className="pmm-mobile-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Ouvrir le menu"
          >
            {sidebarOpen ? <FiX /> : <FiMenu />} Menu
          </button>
        </div>
        <Outlet context={{ stats, refreshStats: loadStats }} />
      </main>
    </div>
  );
}
